import * as React from "react";
import { useCallback, memo, useEffect, useState } from "react";
import { Icon } from "../Icon";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { getVipList } from "../../api/vip";
import { useGlobalValue } from "../../stores/GlobalState";
import { setGlobalValue } from "../../stores/GlobalState";
import { useT } from "../../i18n/hooks";
import type {
  NavigationItem,
  BottomNavigationProps,
} from "./BottomNavigation.types";
import "./BottomNavigation.scss";
import { useCurrencySymbol } from "../../hooks/useUtils";
import {
  useAgentInvitation,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import {
  HomeDefaultIcon,
  HomeActiveIcon,
  EventDefaultIcon,
  EventActiveIcon,
  RewardDefaultIcon,
  RewardActiveIcon,
  ProfileDefaultIcon,
  ProfileActiveIcon,
  BottomNavBgIcon,
} from "../Icon/IconSvgMap";
// 导入图标图片
// import homeDefault from "../../assets/tabbar/home-default.webp";
// import homeActive from "../../assets/tabbar/home-active.webp";
// import eventDefault from "../../assets/tabbar/event-default.webp";
// import eventActive from "../../assets/tabbar/event-active.webp";
// import rewardDefault from "../../assets/tabbar/reward-default.webp";
// import rewardActive from "../../assets/tabbar/reward-active.webp";
// import profileDefault from "../../assets/tabbar/profile-default.webp";
// import profileActive from "../../assets/tabbar/profile-active.webp";
import utils from "../../utils";
import inviteImgDefault from "@/assets/tabbar/invite_img.webp";

const defaultItems: NavigationItem[] = [
  {
    id: "home",
    name: "Home",
    icon: "home",
    iconDefault: HomeDefaultIcon,
    iconActive: HomeActiveIcon,
    badge: "0",
  },
  {
    id: "event",
    name: "Event",
    icon: "event",
    iconDefault: EventDefaultIcon,
    iconActive: EventActiveIcon,
    badge: "0",
  },
  {
    id: "agent-center",
    name: "Invite",
    icon: "invite",
    // Invite 保持原来的特殊样式，不使用图片
    badge: "0",
  },
  {
    id: "vip",
    name: "Reward",
    icon: "reward",
    iconDefault: RewardDefaultIcon,
    iconActive: RewardActiveIcon,
  },
  {
    id: "profile",
    name: "Profile",
    icon: "profile",
    iconDefault: ProfileDefaultIcon,
    iconActive: ProfileActiveIcon,
    badge: "0",
  },
];

export const BottomNavigation: React.FC<BottomNavigationProps> = memo(
  ({
    className = "",
    activeItem = "get-cash",
    onItemClick,
  }: BottomNavigationProps) => {
    const t = useT();
    const currencySymbol = useCurrencySymbol();
    const { getCurrentPath } = useNavigation();
    const [vipBadgeCount] = useGlobalValue("vipBadgeCount");
    const agentInvitation = useAgentInvitation();
    const homeBottomTabAmount = agentInvitation?.homeBottomTabAmount ?? "188";
    const platformLogo = usePlatformLogo();
    const bottomNavInviteRaw = platformLogo?.bottomNavInviteIconUrl?.trim();
    const [inviteIconRemoteFailed, setInviteIconRemoteFailed] = useState(false);

    useEffect(() => {
      setInviteIconRemoteFailed(false);
    }, [bottomNavInviteRaw]);

    const inviteIconSrc =
      bottomNavInviteRaw && !inviteIconRemoteFailed
        ? utils.getImageUrl(bottomNavInviteRaw)
        : inviteImgDefault;

    // 异步获取 VIP Badge 数据
    useEffect(() => {
      const fetchVipBadgeCount = async () => {
        try {
          const response = await getVipList();
          if (response?.data?.data.canClaimCount !== undefined) {
            setGlobalValue("vipBadgeCount", response.data.data.canClaimCount);
          }
        } catch (error) {
          console.error("Failed to fetch VIP badge count:", error);
        }
      };

      const timer = setTimeout(() => {
        fetchVipBadgeCount();
      }, 500);

      return () => clearTimeout(timer);
    }, []);

    const currentPath = getCurrentPath();
    const hideInviteTextRoutes: string[] = [
      ROUTES.EVENT_CENTER,
      ROUTES.VIP,
      ROUTES.PROFILE,
      ROUTES.AGENT_CENTER,
      ROUTES.INVITE_FRIENDS,
    ];
    const shouldShowInviteText =
      (platformLogo?.showBottomNavInviteBonus ?? true) &&
      !hideInviteTextRoutes.includes(currentPath);

    const handleItemClick = useCallback(
      (item: NavigationItem) => {
        item.onClick?.();
        onItemClick?.(item.id);
      },
      [onItemClick]
    );

    const navigationItems = defaultItems.map(item => {
      // 翻译导航项名称
      let translatedName = item.name;
      switch (item.id) {
        case "home":
          translatedName = t("navigation.home");
          break;
        case "event":
          translatedName = t("navigation.events");
          break;
        case "agent-center":
          translatedName = t("agent.invite");
          break;
        case "vip":
          translatedName = t("vip.rewards");
          break;
        case "profile":
          translatedName = t("navigation.profile");
          break;
      }

      if (item.id === "vip") {
        return {
          ...item,
          name: translatedName,
          badge: vipBadgeCount > 0 ? String(vipBadgeCount) : undefined,
        };
      }
      return {
        ...item,
        name: translatedName,
        badge: undefined,
      };
    });

    return (
      <nav className={`bottom-navigation ${className}`}>
        <BottomNavBgIcon className="bottom-navigation__bg" />
        <div className="bottom-navigation__container">
          {navigationItems.map(item => (
            <button
              key={item.id}
              className={`bottom-navigation__item ${
                activeItem === item.id ? "active" : ""
              }`}
              onClick={() => handleItemClick(item)}
              aria-label={item.name}
            >
              <div className="bottom-navigation__icon">
                {item.id === "agent-center" ? (
                  <div className="invite-grp">
                    <img
                      className="invite-grp__img"
                      src={inviteIconSrc}
                      alt=""
                      onError={() => setInviteIconRemoteFailed(true)}
                    />
                    {shouldShowInviteText && (
                      <div className="invite-grp__text">
                        {t("agent.inviteBonus", {
                          symbol: currencySymbol,
                          amount: homeBottomTabAmount || "188",
                        })}
                      </div>
                    )}
                  </div>
                ) : item.iconDefault && item.iconActive ? (
                  // 支持图片路径或 SVG 组件
                  (() => {
                    const icon =
                      activeItem === item.id
                        ? item.iconActive
                        : item.iconDefault;
                    if (typeof icon === "string") {
                      return (
                        <img
                          src={icon}
                          alt={item.name}
                          className="bottom-navigation__icon-img"
                        />
                      );
                    }
                    const SvgIcon = icon as React.ComponentType<{
                      className?: string;
                    }>;
                    return (
                      <SvgIcon
                        className="bottom-navigation__icon-img"
                        // style={{ cursor: "pointer" }}
                      />
                    );
                  })()
                ) : (
                  // 降级使用 Icon 组件
                  <Icon name={item.icon} size={"1.5rem"} />
                )}
              </div>
              <span className="bottom-navigation__label">{item.name}</span>
              {item.badge && item.badge !== "0" && (
                <span className="bottom-navigation__badge">{item.badge}</span>
              )}
            </button>
          ))}
        </div>
      </nav>
    );
  }
);

BottomNavigation.displayName = "BottomNavigation";

export default BottomNavigation;
